import React, {useCallback, useEffect, useMemo, useState} from 'react';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import Layout from '~/pages/templates/DefaultLayout';
import {addressDetails, lawfirm, orderContacts, policySearchRequest} from '~/api';
import {asyncState, useAsync, useAuth, useForm} from '~/hooks';
import {sleep, stringUtils} from '~/util';
import {Card, LoadingSpinner} from '~/components';
import {CheckBox, FormGroup, FormSelect, TextArea} from '~/components/form';
import {attachFile, PolicySearchRequestStatus, SectionHeader} from './common';
import PropertyAddressForm from './PropertyAddressForm';
import OwnerForm from './OwnerForm';
import LegalInformationForm from './LegalInformationForm';
import SearchResults from './SearchResults';
import {MdBook, MdFilterList, MdSearch} from 'react-icons/md';
import { IoMdCheckmarkCircle } from 'react-icons/io';
import {generatePath} from 'react-router-dom';
import {DocumentType} from '~/api/document';
import {DateTime} from 'luxon';
import {useRouter} from '~/hooks/useRouter';
import {GoVerified} from 'react-icons/go';
import {PolicySearchRequestContext} from "~/pages/PolicySearchRequest/context";
import PolicySearchGrid from "~/pages/PolicySearchRequest/PolicySearchGrid";
import PolicyAttachmentsModal from "~/pages/PolicySearchRequest/PolicyAttachmentsModal";
import {isTryonBot} from "~/util/auth";

const defaultState = {
    submitDate: DateTime.now().toJSDate(),
    requestLocked: false,
    readonly: false,
    request: null,
    requestCompleted: false,
    firm: {label: '...', value: ''},
    lawFirms: [],
    requestedBy: {label: '...', value: ''},
    firmUsers: [],
    contacts: [],
    requesterOptions: [],
    showRequesterFields: false,
    requestedBy_FirstName: '',
    requestedBy_LastName: '',
    requestedBy_Email: '',
    requestedBy_needsAccount: false,
    ownerFirstName: '',
    ownerMiddleName: '',
    ownerLastName: '',
    ownerSuffix: '',
    streetNumber: '',
    streetName: '',
    streetSuffix: '',
    city: '',
    county: '',
    state: {label: '...', value: ''},
    stateOptions: [],
    zipcode: '',
    policyYear: '',
    deedYear: '',
    legalDescription: {label: '...', value: ''},
    legalDescriptionOptions: [
        {label: 'Platted', value: 'platted'},
        {label: 'Metes and Bounds', value: 'metesAndBounds'},
        {label: 'Not Provided', value: 'notProvided'}
    ],
    platBook: '',
    platPage: '',
    lot: '',
    subdivision: '',
    block: '',
    section: '',
    notes: '',
    fromList: false,
    metesAndBoundsDescription: '',
    files: [],
    loadingState: asyncState.PENDING,
    editSearch: false,
    filters: {
        ownerName: '',
        address: '',
        county: '',
    },
    filteredRows: []
};

const PolicySearchPage = () => {

    const {user} = useAuth();
    const userRoleIsTryonBot = isTryonBot(user.roleId);
    const [isInitialPolicyLookup, setIsInitialPolicyLookup] = useState(!userRoleIsTryonBot);
    const {query, params, match: {path}, history} = useRouter();
    const {id} = params;
    const viewOnly = query.has('viewOnly')
        ? !!query.get('viewOnly')?.trim()
        : false;

    const editSearch = query.has('editSearch') ? !!query.get('editSearch') : false;

    const gridBackDate = useMemo(() => formatBackDateBySixMonths(), []);
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [usePagination, setUsePagination] = useState(true);
    const [hasFilters, setHasFilters] = useState(true);
    const [showPriorResults, setShowPriorResults] = useState(false);
    const filters = defaultState.filters;
    const [filteredRows, setFilteredRows] = useState(defaultState.filteredRows);
    const firmData = useAsync(() => lawfirm.getFirms(), false);

    useEffect(() => firmData.execute(), []);

    const loadData = (requestId, user) => async (values, props) => {

        const lawFirms = await lawfirm.getFirms()
            .then(firms => firms
                .map(f => ({label: f.Name, value: f.Id}))
            );

        const stateOptions = await loadStates();
        const nc = stateOptions.find(s => s.value === 'NC');

        if (props.id) {
            setIsInitialPolicyLookup(false);
        }

        if (requestId != null) {

            setLoader(true);

            let request, search;

            await Promise.all([
                policySearchRequest.getById(requestId).then(r => request = r),
                policySearchRequest.getSearchResults(requestId).then(s => search = s)
            ]);

            let {_data} = request;

            if (!isEmpty(search)) {
                _data = Object.assign({}, _data, search.searchCriteria);
            }

            const firmId = _data.firmId || _data.requestedBy.firmId;
            const firm = lawFirms.find(f => f.value == firmId);

            const firmUsers = (await lawfirm.getUsersForFirm(firmId))
                .map(u => {
                    return {
                        name: `${u.firstName} ${u.lastName}`,
                        label: <RequesterLabel firstName={u.firstName} lastName={u.lastName} verified/>,
                        value: u.id
                    };
                });

            const contacts = (await orderContacts.getOrderContactsByFirm(firmId))
                .map(c => {
                    return {
                        name: `${c.FirstName} ${c.LastName}`,
                        label: <RequesterLabel firstName={c.FirstName} lastName={c.LastName}/>,
                        value: c.Id
                    };
                });

            let opts = [
                ...firmUsers,
                ...contacts.filter(c => {
                    const taken = firmUsers.findIndex(u => u.contactId == c.Id) > -1;
                    return !taken;
                })
            ];

            opts.sort((a, b) => {
                let aLabel = a.name.toLowerCase();
                let bLabel = b.name.toLowerCase();

                if (aLabel < bLabel) return -1;
                if (bLabel < aLabel) return 1;
                return 0;
            });

            let requesterOptions = opts.map(o => {
                const {name, value} = o;
                return {label: name, value};
            });

            requesterOptions.push({label: 'Add New', value: 'custom'});

            const requesterId = _data.requestedBy.id;

            let showRequesterFields = false;

            let requesterFields = {};

            if (requesterId) {
                requesterFields.requestedBy = firmUsers.find(u => u.value == requesterId);
            } else {
                requesterFields.requestedBy = _data.requestedBy?.contactId != null
                    ? {
                        label: <RequesterLabel firstName={_data.requestedBy.firstName}
                            lastName={_data.requestedBy.lastName}/>, value: _data.requestedBy.contactId
                    }
                    : {label: 'Add New', value: 'custom'};

                requesterFields.requestedBy_Email = stringUtils.hasValue(_data.requestedBy.email)
                    ? _data.requestedBy.email
                    : _data.requestedBy.emailAddress;

                requesterFields.requestedBy_FirstName = _data.requestedBy.firstName;
                requesterFields.requestedBy_LastName = _data.requestedBy.lastName;
                requesterFields.requestedBy_needsAccount = _data.requestedBy['needsAccount'] === undefined
                    ? false
                    : _data.requestedBy.needsAccount;

                showRequesterFields = true;
            }

            const isModifiedByBot = request.statusId === PolicySearchRequestStatus.Review || isTryonBot(request?.modifiedBy?.roleId);
            const requestLocked = request.statusId === PolicySearchRequestStatus.InProgress;
            const requestCompleted = request.completed != null;
            const isLockOwner = user.id == request.modifiedBy.id;

            // Always open in editSearch mode if request was last modified by BOT
            if (isModifiedByBot && !isLockOwner) {
                _data.editSearch = true;
            }

            const requestStatusNew = request.statusId === PolicySearchRequestStatus.New;

            // TODO - Its easier to check if a request can be edit then negate that for readonly.
            const readonly = (requestCompleted && !props.editSearch) || props.viewOnly || (!isModifiedByBot && !isLockOwner && !requestStatusNew) || (requestLocked && !isLockOwner);

            let formState = {
                ...values,
                request: { ...request },
                requestLocked,
                isLockOwner,
                readonly,
                requestCompleted,
                submitDate: DateTime.fromISO(request.submitted).toJSDate(),
                firm,
                firmUsers,
                contacts,
                requesterOptions,
                fromList: _data.fromList || false,
                ownerFirstName: _data.ownerFirstName,
                ownerMiddleName: _data.ownerMiddleName || '',
                ownerLastName: _data.ownerLastName,
                ownerSuffix: _data.ownerSuffix || '',
                streetNumber: _data.streetNumber,
                streetName: _data.streetName,
                streetSuffix: _data.streetSuffix,
                city: _data.city,
                county: _data.county,
                zipcode: _data.zip || _data.zipcode,
                platBook: _data.platBook,
                state: stateOptions.find(s => s.value == _data.state),
                policyYear: _data.policyYear,
                deedYear: _data.deedYear,
                legalDescription: defaultState.legalDescriptionOptions.find(l => l.value == _data.legalDescription)
                    ?? {
                        label: '...',
                        value: ''
                    },
                platPage: _data.platPage,
                lot: _data.lot,
                subdivision: _data.subdivision,
                block: _data.block,
                section: _data.section,
                notes: _data.notes,
                metesAndBoundsDescription: _data.metesAndBoundsDescription,
                ...requesterFields,
                showRequesterFields,
                editSearch: isModifiedByBot ? _data.editSearch : props.editSearch
            };

            setLoader(false);

            return {
                ...formState,
                lawFirms,
                stateOptions,
                result: isEmpty(search) ? null : {...search},
                loadingState: asyncState.SUCCESS
            };
        }

        return {
            ...values,
            lawFirms,
            stateOptions,
            state: isInitialPolicyLookup ? '' : nc,
            loadingState: asyncState.SUCCESS
        };
    };

    const onSubmit = async ({values, onInvalidForm, resetFormErrors, onFail}) => {
        try {

            if (isInitialPolicyLookup) {
                return;
            }

            resetFormErrors();
            const errors = validatePolicySearch(values);
            if (Object.keys(errors).length > 0) {
                return onInvalidForm(errors);
            }

            const {
                state,
                legalDescription,
                firm,
                files,
                requestedBy,
                ...otherData
            } = values;

            const firmId = firm.value;
            const lawFirm = firm.label;

            const localOnlyState = [
                'stateOptions',
                'userRoleIsTryonBot',
                'legalDescriptionOptions',
                'lawFirms',
                'firmUsers',
                'contacts',
                'requesterOptions',
                'requestedBy_Email',
                'requestedBy_FirstName',
                'requestedBy_LastName',
                'showRequesterFields',
                'request',
                'result',
                'requestLocked',
                'readonly',
                'isComplete',
                'requestCompleted',
                'searchResults',
                'loadingState'
            ];

            const data = {
                ...omit(otherData, localOnlyState),
                state: state.value,
                firmId,
                lawFirm,
                legalDescription: legalDescription.value
            };

            let {request} = values;

            if (!request || Object.keys(request) < 1 || otherData.editSearch) {
                if (/[a-zA-Z]/i.test(requestedBy.value)) {
                    const customRequestedBy = {
                        firstName: values.requestedBy_FirstName,
                        lastName: values.requestedBy_LastName,
                        email: values.requestedBy_Email,
                        needsAccount: values.requestedBy_needsAccount,
                        firmId
                    };

                    if (requestedBy.value != 'custom') {
                        customRequestedBy.contactId = requestedBy.value;
                    }

                    /**************************************************
                     * Putting this back in case we want it later
                     * This will automagically create a user account
                     * whenever the `needsAccount` checkbox is true
                     **************************************************/
                    // if (requestedBy_needsAccount) {
                    //     await auth.registerUser({
                    //         ...customRequestedBy,
                    //         emailAddress: values.requestedBy_Email,
                    //         lawFirm: values.lawFirms.find(l => l.value == firmId)?.label,
                    //         roleId: ROLES.FIRM_USER,
                    //         active: false,
                    //         userName: `${customRequestedBy.firstName.charAt(0)}${customRequestedBy.lastName}_generated`
                    //     });
                    // }

                    data.requestedBy = customRequestedBy;

                } else {
                    data.requestedBy = {id: Number(requestedBy.value)};
                }

                if (otherData.editSearch) {
                    let updateData = {
                        ...data,
                        statusId: userRoleIsTryonBot ? PolicySearchRequestStatus.Review : PolicySearchRequestStatus.InProgress,
                        completed: null
                    };
                    request = await policySearchRequest.completeSearch(request.id, updateData);

                    await Promise.all(
                        files.map(attachFile(request.id, DocumentType.DEED))
                    );
                } else {
                    request = await policySearchRequest.create(data);

                    await Promise.all(
                        files.map(attachFile(request.id, DocumentType.DEED))
                    );

                    await policySearchRequest.sendNewRequestEmail(request.id);
                }

            }

            await policySearchRequest.runSearch(request.id, data);

            return request;

        } catch (err) {
            console.error(err);
            return onFail(err.message);
        }

    };

    const validatePolicySearch = (data) => {

        if (isInitialPolicyLookup) {
            return {};
        }

        if (!userRoleIsTryonBot) {
            if (!data.firm?.value) {
                return {firm: 'Please select the Law Firm submitting this request'};
            }

            if (!data.requestedBy.value) {
                return {requestedBy: 'Please select the requester'};
            }

            if (!data.legalDescription.value) {
                return {legalDescription: 'Please select a Legal Description'};
            }
        }

        /*if(data.contacts.filter(x => x.Id == data.requestedBy.value).length <= 0 && data.firmUsers.filter(x => x.id == data.requestedBy.value).length <= 0) {
            return { requestedBy: 'Please select correct requester' };
        } */

        const requiredFields = [
            'streetNumber',
            'streetName',
            'zipcode',
            'city',
            'county'
        ];

        if (data.requestedBy.value == 'custom') {
            requiredFields.push(
                'requestedBy_FirstName',
                'requestedBy_LastName',
                'requestedBy_Email'
            );
        }

        if (!userRoleIsTryonBot) {
            requiredFields.push('submitDate', 'firm');

            if (data.legalDescription.value == 'metesAndBounds') {
                requiredFields.push('metesAndBoundsDescription');
            }
        }

        return requiredFields.reduce((acc, field) => {
            const value = data[field];
            if (!stringUtils.hasValue(value)) {
                acc[field] = 'This is a required field';
            }
            return acc;
        }, {});
    };

    const handlePageSizeChange = newPageSize => {
        setPageSize(newPageSize);
        gridData.execute();
    };

    const handlePageChange = nextPage => {
        setPage(nextPage);
        gridData.execute();
    };

    useEffect(() => {
        if (!isInitialPolicyLookup) {
            setShowPriorResults(false);
        }
    }, [isInitialPolicyLookup]);

    const gridData = useAsync(async (params) => {

        setShowPriorResults(true);
        setLoader(true);

        const nextPage = (params?.page || params?.page === 0) ? params.page : page;
        const offset = getOffset(nextPage, pageSize);

        const requests = await policySearchRequest
            .getAll(gridBackDate, offset?.offset, offset?.size, hasFilters, formatFilters(filters));

        if (Array.isArray(requests)) {
            setTotal(requests[0]?.total);
            setFilteredRows(requests);
        } else {
            setTotal(0);
        }

        setLoader(false);

        return requests.sort((a, b) => {
            const aModified = DateTime.fromISO(a.modified);
            const bModified = DateTime.fromISO(b.modified);

            if (aModified > bModified)
                return -1;
            if (aModified < bModified)
                return 1;
            return 0;
        });
    }, false);

    const [attachmentsOpen, setAttachmentsOpen] = useState(false);
    const showAttachmentsModal = () => setAttachmentsOpen(true);
    const hideAttachmentsModal = () => setAttachmentsOpen(false);
    const [attachments, setAttachments] = useState([]);

    const continuePolicySearch = async () => {
        
        // make it clear to the user that something has changed
        // rather than just snapping to the top of the page
        setLoader(true);
        await new Promise(resolve => setTimeout(resolve, 500));

        setIsInitialPolicyLookup(false);
        window.scrollTo({ top: 0});

        setLoader(false);
    };

    const onLoad = useCallback(loadData(id, user), [id, user]);

    const {
        state,
        fieldActions,
        formActions
    } = useForm({
        initialState: {...defaultState},
        props: {id, viewOnly, editSearch},
        onSubmit,
        onLoad
    });

    const clearFilters = () => {

        formActions.reset();
        setShowPriorResults(false);
        setFilteredRows([]);
        setUsePagination(true);
        setPage(0);

        window.location = '/policySearch';
    };

    const {
        status: submitStatus, execute: submit
    } = useAsync(async () => {
        await formActions.submit(request => {

            if (isInitialPolicyLookup) {

                filters.county = state.values.county ?? '';

                // build address from street no, street name and suffix
                filters.address = [state.values.streetNumber, state.values.streetName, state.values.streetSuffix].filter(Boolean).join(" ");

                // build ownerName from - suffix, first name, middle and last name
                filters.ownerName = [state.values.ownerSuffix ?? '', state.values.ownerFirstName, state.values.ownerMiddleName, state.values.ownerLastName].filter(Boolean).join(" ");

                setHasFilters(true);
                setPage(0);
                gridData.execute();

            } else if (id) {
                policySearchRequest.getSearchResults(request.id).then(r => {
                    fieldActions.onChange(r, 'result');
                });
            } else {
                const newPath = generatePath(path, {id: request.id});
                history.replace(newPath);
            }
        });
    }, false);

    const isComplete = useMemo(() => state.values.requestCompleted, [state.values]);
    const readonly = useMemo(() => state.values.readonly, [state.values]);

    const setLoader = (loading = false) => {
        if (loading) {
            fieldActions.onChange(asyncState.PENDING, 'loadingState');
        } else {
            fieldActions.onChange(asyncState.SUCCESS, 'loadingState');
        }
    };

    const onComplete = () => {
        window.scrollTo(0, 0);
        history.go(0);
    };

    const populateRequestorListForFirm = async (firmId) => {
        let contacts = [];
        let firmUsers = [];

        await Promise.all([
            orderContacts.getOrderContactsByFirm(firmId).then(c => contacts.push(...c)),
            lawfirm.getUsersForFirm(firmId).then(u => firmUsers.push(...u))
        ]);
        let requesterOptions = firmUsers
            .map(u => {
                return {
                    name: `${u.firstName} ${u.lastName}`,
                    label: <RequesterLabel firstName={u.firstName} lastName={u.lastName} verified/>,
                    value: u.id
                };
            });

        requesterOptions.push(
            ...contacts.filter(c => {
                const taken = firmUsers.findIndex(u => u.contactId == c.Id) > -1;
                return !taken;
            }).map(c => {
                return {
                    name: `${c.FirstName} ${c.LastName}`,
                    label: <RequesterLabel firstName={c.FirstName} lastName={c.LastName}/>,
                    value: c.Id
                };
            }));

        requesterOptions.sort((a, b) => {
            let aLabel = a.name.toLowerCase();
            let bLabel = b.name.toLowerCase();

            if (aLabel < bLabel) return -1;
            if (bLabel < aLabel) return 1;
            return 0;
        }).map(o => {
            const {label, value} = o;
            return {label, value};
        });

        requesterOptions.push({label: 'Add New', value: 'custom'});

        fieldActions.onChange(firmUsers, 'firmUsers');
        fieldActions.onChange(contacts, 'contacts');
        fieldActions.onChange(requesterOptions, 'requesterOptions');
    };

    const Completed = ({request = {}}) => {
        const {
            status,
            completed,
            modifiedBy
        } = request;


        const completeDate = DateTime.fromJSDate(new Date(completed));
        const submitDate = DateTime.fromJSDate(new Date(request.submitted));
        const turnTime = completeDate.diff(submitDate, ['hours', 'minutes', 'seconds']).toObject();

        return (
            <div className="text-green-900 bg-green-10 rounded-md p-1 py-2 text-sm font-medium flex justify-between">
                <p className="flex items-center">
                    <IoMdCheckmarkCircle className='mr-1 ml-2'/>
                    Completed (Marked as {status})
                    on {completeDate.toLocaleString(DateTime.DATE_SHORT)}{' '}
                    at {completeDate.toLocaleString(DateTime.TIME_SIMPLE)}{' '}
                    by {`${modifiedBy.firstName} ${modifiedBy.lastName}`}{'. '}
                    Search turn time was {turnTime.hours}h {turnTime.minutes}m {Math.floor(turnTime.seconds)}s.
                </p>

                <button
                    className='py-2 px-4 flex items-center justify-center bg-blue-50 hover:bg-blue-60 text-white text-sm rounded-full'
                    onClick={clearFilters}
                >
                    <MdBook className="mr-2" size="1.025em"/>
                    New Prior Policy Search
                </button>

            </div>
        );
    };

    const ctxValue = {
        gridData,
        firmData,
        filteredRows,
        filters,
        fieldActions,
        setLoader,
        attachments,
        setAttachments,
        showAttachmentsModal,
        hideAttachmentsModal
    };

    return (
        <PolicySearchRequestContext.Provider value={ctxValue}>
            <Layout title="Prior Policy Search" subtitle="Search for prior policies on the requested property">
                <div className="container h-full flex flex-col justify-center items-stretch relative">
                    <Card className="p-6 mb-12 mt-12 flex-1 rounded-md flex flex-col">
                        <LoadingSpinner status={state.values.loadingState}/>
                        {isComplete && <Completed request={state.values.request} search={state.values.result}/>}
                        <LoadingSpinner status={submitStatus}/>
                        <div
                            className="mt-8 grid grid-cols-1 lg:grid-cols-8 grid-flow-row auto-rows-auto gap-x-8 gap-y-4">

                            {!isInitialPolicyLookup && (
                                <>
                                    <FormGroup
                                        label="Date &amp; Time Submitted"
                                        type='date-picker'
                                        name='submitDate'
                                        required={!userRoleIsTryonBot}
                                        readOnly={readonly}
                                        timeInputLabel="Time:"
                                        dateFormat="MM/dd/yyyy   h:mm aa"
                                        showTimeInput
                                        groupClassName="col-span-4"
                                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                                        datePickerClasses="w-full"
                                        path='submitDate'
                                        value={state.values.submitDate}
                                        error={state.errors.submitDate}
                                        {...fieldActions}
                                        validator={userRoleIsTryonBot || isInitialPolicyLookup ? undefined : notEmpty}
                                    />
                                    <FormSelect
                                        label={`Firm Name ${userRoleIsTryonBot ? '' : '*'}`}
                                        required={!userRoleIsTryonBot}
                                        readOnly={readonly}
                                        isSearchable
                                        options={state.values.lawFirms}
                                        value={state.values.firm}
                                        error={state.errors.firm}
                                        groupClassName="col-span-4"
                                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                                        path='firm'
                                        {...fieldActions}
                                        onChange={(firm) => {
                                            fieldActions.onChange(firm, 'firm');
                                            fieldActions.onChange({label: '...', value: ''}, 'requestedBy');

                                            populateRequestorListForFirm(firm.value);
                                        }}
                                    />
                                    <FormSelect
                                        label={`Requested By ${userRoleIsTryonBot ? '' : '*'}`}
                                        required={!userRoleIsTryonBot}
                                        isSearchable
                                        readOnly={readonly || !(state.values.firm && state.values.firm.value)}
                                        options={state.values.requesterOptions}
                                        groupClassName="col-span-4"
                                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                                        path='requestedBy'
                                        value={state.values.requestedBy}
                                        error={state.errors.requestedBy}
                                        {...fieldActions}
                                        onChange={requester => {
                                            const contactPattern = /[a-zA-Z]/i;
                                            if (contactPattern.test(requester.value)) {
                                                fieldActions.onChange(true, 'showRequesterFields');

                                                if (requester.value != 'custom') {
                                                    const contact = state.values.contacts.find(c => c.Id == requester.value);

                                                    fieldActions.onChange(contact.Email, 'requestedBy_Email');
                                                    fieldActions.onChange(contact.FirstName, 'requestedBy_FirstName');
                                                    fieldActions.onChange(contact.LastName, 'requestedBy_LastName');
                                                    fieldActions.onChange(false, 'requestedBy_needsAccount');
                                                } else {
                                                    fieldActions.onChange('', 'requestedBy_Email');
                                                    fieldActions.onChange('', 'requestedBy_FirstName');
                                                    fieldActions.onChange('', 'requestedBy_LastName');
                                                    fieldActions.onChange(true, 'requestedBy_needsAccount');
                                                }
                                            } else {
                                                fieldActions.onChange('', 'requestedBy_Email');
                                                fieldActions.onChange('', 'requestedBy_FirstName');
                                                fieldActions.onChange('', 'requestedBy_LastName');
                                                fieldActions.onChange(false, 'requestedBy_needsAccount');
                                                fieldActions.onChange(false, 'showRequesterFields');
                                            }
                                            fieldActions.onChange(requester, 'requestedBy');
                                        }}
                                    />
                                    {state.values.showRequesterFields && (
                                        <React.Fragment>
                                            <FormGroup
                                                label="Requester's Email"
                                                required={!userRoleIsTryonBot}
                                                readOnly={readonly}
                                                groupClassName="col-span-3"
                                                inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                                                path="requestedBy_Email"
                                                value={state.values.requestedBy_Email}
                                                error={state.errors.requestedBy_Email}
                                                {...fieldActions}
                                            />
                                            <CheckBox
                                                label="Needs Account"
                                                readOnly={readonly}
                                                value={state.values.requestedBy_needsAccount}
                                                path="requestedBy_needsAccount"
                                                {...fieldActions}
                                                groupClassName="col-span-1 flex items-center text-sm"
                                                inputClassName="border-1 border-gray-200 rounded bg-white mr-2"
                                            />
                                            <FormGroup
                                                label="Requester's First Name"
                                                required={!userRoleIsTryonBot}
                                                readOnly={readonly}
                                                groupClassName="col-span-2 col-start-1"
                                                inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                                                path="requestedBy_FirstName"
                                                value={state.values.requestedBy_FirstName}
                                                error={state.errors.requestedBy_FirstName}
                                                {...fieldActions}
                                            />
                                            <FormGroup
                                                label="Requester's Last Name"
                                                required={!userRoleIsTryonBot}
                                                readOnly={readonly}
                                                groupClassName="col-span-2"
                                                inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                                                path="requestedBy_LastName"
                                                value={state.values.requestedBy_LastName}
                                                error={state.errors.requestedBy_LastName}
                                                {...fieldActions}
                                            />
                                        </React.Fragment>
                                    )}
                                    <CheckBox
                                        type="checkbox"
                                        label="Search Submitted as Part of a List"
                                        readOnly={readonly}
                                        value={state.values.fromList}
                                        path="fromList"
                                        {...fieldActions}
                                        groupClassName="col-span-2 flex items-center text-sm"
                                        inputClassName="border-1 border-gray-200 rounded bg-white mr-2"
                                    />
                                </>
                            )}

                            {isInitialPolicyLookup && (
                                <>
                                    <p className={'font-semibold col-span-8'}>Lookup Previous Prior Search
                                        Submissions <span className="font-light italic">rolling 6 months</span></p>
                                </>
                            )}

                            <SectionHeader colSpan={8}>Owner Name</SectionHeader>
                            <OwnerForm state={state} fieldActions={fieldActions} readonly={readonly}/>

                            <SectionHeader colSpan={8}>Property Address</SectionHeader>
                            <PropertyAddressForm state={state} fieldActions={fieldActions} formActions={formActions}
                                readonly={readonly} setLoader={setLoader}
                                indicateWhenFieldsAreRequired={!isInitialPolicyLookup}/>

                            {!isInitialPolicyLookup && (
                                <>
                                    <SectionHeader colSpan={8}>Legal Information</SectionHeader>
                                    <LegalInformationForm className='col-span-8' state={state}
                                        fieldActions={fieldActions} readonly={readonly} legalDescriptionIsOptional={userRoleIsTryonBot}/>

                                    <TextArea
                                        type="textarea"
                                        label="Notes"
                                        name="notes"
                                        path='notes'
                                        readOnly={readonly}
                                        groupClassName="col-span-8"
                                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                                        {...fieldActions}
                                        value={state.values.notes}
                                        error={state.errors.notes}
                                    />
                                </>
                            )}
                        </div>
                        {(!state.values.requestCompleted || state.values.editSearch) && (
                            <div className="flex justify-end items-center pt-8 mt-auto">
                                {isInitialPolicyLookup && (
                                    <button
                                        className='py-2 px-4 flex text-white text-sm font-semibold focus:outline-none items-center bg-lightGreen-300 justify-center rounded-full'
                                        onClick={submit}
                                    >
                                        <MdFilterList size='1.125em' className='mr-1'/>
                                        Filter
                                    </button>
                                )}

                                {!isInitialPolicyLookup && (
                                    <button
                                        className='py-3 px-5 ml-4 rounded-full text-white flex items-center focus:outline-none bg-lightGreen-300 disabled:opacity-50'
                                        disabled={Object.keys(state.errors).length || readonly}
                                        onClick={submit}
                                    >
                                        {state.values.editSearch ? "Update" : "Search"}
                                        <MdSearch className="ml-2" size="1.25rem"/>
                                    </button>
                                )}
                            </div>
                        )}
                        {state.values.result && (
                            <SearchResults
                                request={state.values.request}
                                result={state.values.result}
                                isComplete={readonly}
                                showSaveButton={userRoleIsTryonBot}
                                setLoader={setLoader}
                                onComplete={onComplete}
                                requestedBy_needsAccount={state.values.requestedBy_needsAccount}
                                className="mt-4"/>
                        )}
                        {showPriorResults && (
                            <>
                                <PolicySearchGrid
                                    pageSize={pageSize}
                                    currentPage={page}
                                    handlePageChange={handlePageChange}
                                    totalRows={total}
                                    handlePageSizeChange={handlePageSizeChange}
                                    usePagination={usePagination}
                                />
                                <PolicyAttachmentsModal isOpen={attachmentsOpen}/>

                                <hr/>

                                <div className="flex justify-end items-center pt-8 mt-auto">

                                    <button
                                        className='py-2 px-4 ml-4 flex items-center justify-center bg-lightGreen-300 hover:bg-lightGreen-300 text-white text-sm rounded-full'
                                        onClick={clearFilters}
                                    >
                                        <MdBook className="mr-2" size="1.025em"/>
                                        Clear Filter Criteria
                                    </button>

                                    <button
                                        className='py-2 px-4 flex ml-4 items-center justify-center bg-blue-50 hover:bg-blue-60 text-white text-sm rounded-full'
                                        onClick={continuePolicySearch}
                                    >
                                        <MdBook className="mr-2" size="1.025em"/>
                                        Cont. Prior Policy Search
                                    </button>

                                </div>
                            </>
                        )}
                    </Card>
                </div>
            </Layout>
        </PolicySearchRequestContext.Provider>
    );
};

const notEmpty = val => {
    return /(.|\s)*\S(.|\s)*/.test(val) ? true : 'This is a required field';
};

const loadStates = async () => {
    await sleep(40);
    return addressDetails.getStateCodes();
};
const getOffset = (page, pageSize = 10) => {
    return {
        offset: page * pageSize,
        size: pageSize
    };
};

function RequesterLabel({firstName, lastName, verified = false}) {
    return (
        <span className='flex items-center'>
            {`${firstName} ${lastName}`}
            {verified && (
                <GoVerified className='text-primary-50 ml-2'/>
            )}
        </span>
    );
}

function formatBackDateBySixMonths() {
    const currentDate = DateTime.utc().set({hour: 0, minute: 0, second: 0, millisecond: 0});
    const backDate = currentDate.minus({months: 6});

    return backDate ? backDate.toISO() : null;
}

function formatFilters(filters) {
    return {
        ownerName: filters?.ownerName || '',
        address: filters?.address || '',
        county: filters?.county || '',
    };
}

export default PolicySearchPage;